import React, { useState } from 'react';
import { Database, Lock, LogOut } from 'lucide-react';

export default function DatabaseAdmin() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);

  const email = 'techin@tmhwst.toyota-industries.com'; // Fixed to user

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/sysadmin/tables', {
        headers: {
          'X-Admin-Email': email,
          'X-Admin-Password': password
        }
      });
      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setIsAuthenticated(true);
        setTables(data.data || []);
      } else {
        setError(data.message || 'Invalid password');
      }
    } catch (err) {
      setError('Network error connecting to database admin');
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (tableName) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/sysadmin/tables/${tableName}`, {
        headers: {
          'X-Admin-Email': email,
          'X-Admin-Password': password
        }
      });
      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setColumns(data.columns || []);
        setTableData(data.data || []);
        setSelectedTable(tableName);
      } else {
        setError(data.message || `Failed to fetch data for ${tableName}`);
      }
    } catch (err) {
      setError('Network error fetching table data');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', background: '#f3f4f6' }}>
        <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)', width: '100%', maxWidth: '400px', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', background: '#fee2e2', padding: '1rem', borderRadius: '50%', marginBottom: '1.5rem', color: '#ef4444' }}>
            <Lock size={32} />
          </div>
          <h2 style={{ marginBottom: '0.5rem', color: '#1f2937' }}>System Admin</h2>
          <p style={{ color: '#6b7280', marginBottom: '2rem', fontSize: '0.9rem' }}>Direct database access requires elevated privileges.</p>
          
          <form onSubmit={handleLogin}>
            <input 
              type="password" 
              placeholder="Enter Admin Password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '1rem', fontSize: '1rem' }}
              autoFocus
            />
            {error && <p style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem', textAlign: 'left' }}>{error}</p>}
            <button 
              type="submit" 
              disabled={loading}
              style={{ width: '100%', padding: '0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '600', fontSize: '1rem', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Verifying...' : 'Access Database'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <div style={{ padding: '1.5rem 2rem', background: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: '#ef4444', color: 'white', padding: '0.5rem', borderRadius: '8px' }}>
            <Database size={24} />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#111827' }}>Database Viewer</h1>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>Read-only access to raw database tables</p>
          </div>
        </div>
        <button 
          onClick={() => { setIsAuthenticated(false); setPassword(''); setTableData([]); setSelectedTable(null); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f3f4f6', border: '1px solid #e5e7eb', padding: '0.5rem 1rem', borderRadius: '6px', color: '#4b5563', cursor: 'pointer', fontWeight: 500 }}
        >
          <LogOut size={16} /> Exit Admin
        </button>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar for Tables */}
        <div style={{ width: '280px', background: 'white', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
            <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tables ({tables.length})</h3>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {tables.map(t => (
              <button
                key={t}
                onClick={() => fetchTableData(t)}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  borderBottom: '1px solid #f3f4f6',
                  background: selectedTable === t ? '#eff6ff' : 'white',
                  color: selectedTable === t ? '#2563eb' : '#374151',
                  fontWeight: selectedTable === t ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Data Grid */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {error && (
            <div style={{ padding: '1rem', background: '#fee2e2', color: '#991b1b', borderBottom: '1px solid #f87171' }}>
              {error}
            </div>
          )}
          
          {!selectedTable ? (
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#9ca3af' }}>
              Select a table from the sidebar to view its data.
            </div>
          ) : (
            <>
              <div style={{ padding: '1rem 2rem', background: 'white', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0, color: '#1f2937' }}>Table: <span style={{ color: '#2563eb', fontFamily: 'monospace' }}>{selectedTable}</span></h3>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', background: '#f3f4f6', padding: '0.2rem 0.6rem', borderRadius: '12px' }}>
                  {loading ? 'Loading...' : `${tableData.length} records`}
                </span>
              </div>
              
              <div style={{ flex: 1, overflow: 'auto', padding: '1rem 2rem' }}>
                {tableData.length === 0 && !loading ? (
                  <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280', background: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                    No data found in this table.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', background: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.85rem' }}>
                      <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <tr>
                          {columns.map(col => (
                            <th key={col} style={{ padding: '0.75rem 1rem', fontWeight: 600, color: '#4b5563', whiteSpace: 'nowrap' }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            {columns.map(col => (
                              <td key={`${i}-${col}`} style={{ padding: '0.75rem 1rem', color: '#1f2937', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row[col] !== null ? String(row[col]) : <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>NULL</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
